/*
 * Pick a skill or job category and see, for every live page in it, which of its target keywords
 * the page actually carries and whether Google shows the page for each one.
 *
 * The categories are the site's own roles (seoFacets.roles), which are the tracker sheet's
 * "Skill / Job Category" column: the site's keywords are pinned to that sheet by a test there.
 */
import AutocompleteFormField from '@components/Common/AutocompleteFormField';
import DetailSection from '@components/Common/DetailSection';
import EmptyState from '@components/Common/EmptyState';
import ErrorMessage from '@components/Common/ErrorMessage';
import RecordLink from '@components/Common/RecordLink';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { formatDateTime } from '@helpers/date';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import { Box } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import SeoKeywordChecks, { coverageLabel } from './SeoKeywordChecks';
import {
  type SeoFacet,
  type SeoKeyword,
  type SeoPage,
  fetchFacets,
  fetchPageQueries,
  fetchRolePages,
  fmtCount,
  pageIdOf,
} from './seoData';

/** The Mumbai role page first, then areas, then anything national, each alphabetically. */
const pageOrder = (p: SeoPage) =>
  `${p.locality ? 1 : p.city ? 0 : 2}${p.path}`;

const PageCard: React.FC<{ page: SeoPage; queries: SeoKeyword[] }> = ({ page, queries }) => {
  const meta = [
    page.liveJobs !== null ? `${fmtCount(page.liveJobs)} live jobs` : null,
    page.indexable === false ? 'Not indexable (too few jobs)' : null,
    coverageLabel(page.keywordChecks),
    page.lastSyncedAt ? `Read ${formatDateTime(page.lastSyncedAt)}` : null,
  ].filter(Boolean);
  return (
    <DetailSection
      title={
        <span className="seo-cat__title">
          <RecordLink to={`/seo/pages/${pageIdOf(page.path)}`} label={`Open ${page.path}`}>
            {page.path}
          </RecordLink>
          <span className="seo-cat__meta">{meta.join(' · ')}</span>
        </span>
      }
    >
      <SeoKeywordChecks
        checks={page.keywordChecks}
        queries={queries}
        hasKeywords={!!page.primaryKeyword || page.secondaryKeywords.length > 0}
      />
    </DetailSection>
  );
};

const SeoCategoriesTab: React.FC<{
  role: string;
  setRole: (role: string) => void;
  reloadKey: number;
}> = ({ role, setRole, reloadKey }) => {
  const [roles, setRoles] = useState<SeoFacet[]>([]);
  const [pages, setPages] = useState<SeoPage[] | null>(null);
  const [queries, setQueries] = useState<Record<string, SeoKeyword[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFacets()
      .then((f) => setRoles(f?.roles ?? []))
      .catch(() => setRoles([]));
  }, [reloadKey]);

  const latest = useRef(0);
  const load = useCallback(async () => {
    if (!role) {
      setPages(null);
      return;
    }
    const ticket = ++latest.current;
    setLoading(true);
    setError(null);
    try {
      const found = (await fetchRolePages(role)).sort((a, b) =>
        pageOrder(a).localeCompare(pageOrder(b)),
      );
      // Search Console per page, side by side: a category has a handful of pages, not hundreds.
      const perPage = await Promise.all(
        found.map((p) => fetchPageQueries(p.path).catch(() => [] as SeoKeyword[])),
      );
      if (ticket !== latest.current) return;
      setPages(found);
      setQueries(Object.fromEntries(found.map((p, i) => [p.path, perPage[i]])));
    } catch {
      if (ticket === latest.current) setError('The pages for this category could not be loaded.');
    } finally {
      if (ticket === latest.current) setLoading(false);
    }
  }, [role]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const selected = role
    ? (roles.find((r) => r.value === role) ?? { value: role, count: 0 })
    : null;

  return (
    <Box className="seo-list__tab">
      <Box className="seo-cat__picker">
        <AutocompleteFormField
          required={false}
          name="seo-category"
          label="Skill / job category"
          size="small"
          placeholder="Choose a category, e.g. Jewellery Sales"
          options={roles}
          value={selected}
          getOptionLabel={(o: SeoFacet) => (o ? `${o.value} (${o.count})` : '')}
          isOptionEqualToValue={(o: SeoFacet, v: SeoFacet) => o?.value === v?.value}
          onChange={(_e: unknown, nv: SeoFacet | null) => setRole(nv?.value ?? '')}
        />
      </Box>

      {!role ? (
        <EmptyState
          icon={<CategoryOutlinedIcon />}
          title="Choose a category"
          body="See every live page for it, which of its keywords each page carries, and where Google shows it for them."
        />
      ) : error ? (
        <ErrorMessage message={error} onRetry={load} retryLabel="Try again" />
      ) : loading || !pages ? (
        <SkeletonLoader rows={6} columns={9} />
      ) : pages.length === 0 ? (
        <EmptyState
          icon={<CategoryOutlinedIcon />}
          title="No live page for this category"
          body="The site has no landing page for it yet, or the last sync has not read it."
        />
      ) : (
        pages.map((p) => <PageCard key={p.path} page={p} queries={queries[p.path] ?? []} />)
      )}
    </Box>
  );
};

export default SeoCategoriesTab;
